package com.wintermar.inspection.feature.data.remote.mapper.answer

import com.apollographql.apollo.api.Optional
import com.wintermar.inspection.GetAnswersByInspectionId
import com.wintermar.inspection.GetByUploadStatuses
import com.wintermar.inspection.GetFailedSubmissions
import com.wintermar.inspection.core.ui.component.image.util.FileChunkModel
import com.wintermar.inspection.core.ui.component.image.util.FileUploadStatus
import com.wintermar.inspection.feature.domain.model.answer.AnswerFileModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerLocationModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerSelectionModel
import com.wintermar.inspection.graphql.type.FindingCategory
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import com.wintermar.inspection.graphql.type.InspectionMediaDtoInput
import com.wintermar.inspection.graphql.type.UpsertInspectionCommandInput

fun List<GetAnswersByInspectionId>.toListAnswerModel(): List<AnswerModel> {
    val grouped = this.groupBy { it.answerId }
    return grouped.map { (_, rows) ->
        val firstRow = rows.first()
        val files = rows.mapNotNull { row ->
            row.fileId?.let {
                AnswerFileModel(
                    id = it,
                    answerId = row.answerId,
                    name = row.fileName ?: "",
                    originalName = row.fileOriginalName ?: "",
                    path = row.filePath ?: "",
                    size = row.fileSize ?: 0,
                    type = row.fileType ?: "",
                    blob = row.fileBlob,
                    uploadStatus = if (row.fileStatus == null) FileUploadStatus.NONE else FileUploadStatus.valueOf(
                        row.fileStatus
                    ),
                    hasGenerateChunk = row.fileHasGenerateChunk ?: false
                )
            }
        }.distinctBy { it.id }
        AnswerModel(
            id = firstRow.answerId,
            inspectionId = firstRow.inspectionId,
            questionId = firstRow.questionId,
            notes = firstRow.notes ?: "",
            isRequiredFileOrNotes = firstRow.isRequiredFileOrNotes ?: false,
            answerDateIso = firstRow.answerDateIso ?: "",
            answerOptionId = firstRow.answerOptionId ?: "",
            answerText = firstRow.answerText ?: "",
            answerLocation = firstRow.locationId?.let {
                AnswerLocationModel(
                    id = it,
                    answerId = firstRow.answerId,
                    name = firstRow.locationName ?: "",
                    latitude = firstRow.latitude ?: 0.0,
                    longitude = firstRow.longitude ?: 0.0,
                )
            } ?: AnswerLocationModel(),
            answerSelection = firstRow.selectionId?.let {
                AnswerSelectionModel(
                    answerId = firstRow.answerId,
                    id = it,
                    answerSelectionId = firstRow.answerSelectionId ?: "",
                    answerSelectionValue = firstRow.answerSelectionValue ?: ""
                )
            } ?: AnswerSelectionModel(),
            files = files,
            lastUpdatedIsoUtc0 = firstRow.lastUpdated ?: "",
            isSubmitted = firstRow.isSubmitted ?: false,
            findingCategory = firstRow.findingCategory?.let {
                FindingCategory.safeValueOf(
                    it
                )
            }
        )
    }
}

fun AnswerModel.toUpsertInspectionCommandInput(answerType: InspectionAnswerType): UpsertInspectionCommandInput {
    return UpsertInspectionCommandInput(
        id = this.id,
        inspectionId = this.inspectionId,
        questionId = this.questionId,
        answer = Optional.presentIfNotNull(this.getAnswerByType(answerType)),
        optionId = if (this.answerOptionId.isNotBlank()) Optional.present(this.answerOptionId) else Optional.Absent,
        isFlagged = Optional.presentIfNotNull(this.isRequiredFileOrNotes),
        notes = if (this.notes.isNotBlank()) Optional.present(this.notes) else Optional.Absent,
        findingCategory = if (this.findingCategory != null && this.isRequiredFileOrNotes) Optional.present(
            this.findingCategory
        ) else Optional.Absent,
        media = if (this.files.isNotEmpty()) Optional.present(this.files.toMediaCommandInput()) else Optional.Absent,
    )
}

fun List<AnswerFileModel>.toMediaCommandInput(): List<InspectionMediaDtoInput> {
    return this.map {
        InspectionMediaDtoInput(
            id = it.id,
            fileName = it.name,
            originalFileName = it.originalName,
            size = it.size.toInt()
        )
    }
}

fun List<GetByUploadStatuses>.toListAnswerModelFromUploadStatuses(): List<AnswerModel> {
    val grouped = this.groupBy { it.answerId }
    return grouped.map { (_, rows) ->
        val firstRow = rows.first()
        AnswerModel(
            id = firstRow.answerId,
            inspectionId = firstRow.inspectionId,
            questionId = firstRow.questionId,
            files = rows
                .groupBy { it.fileId }
                .map { (fileId, fileRows) ->
                    val fileFirst = fileRows.first()
                    AnswerFileModel(
                        id = fileId,
                        answerId = firstRow.answerId,
                        name = fileFirst.name,
                        originalName = fileFirst.originalName ?: "",
                        path = fileFirst.path ?: "",
                        size = fileFirst.fileSize ?: 0L,
                        type = fileFirst.type ?: "",
                        blob = fileFirst.blob,
                        uploadStatus = if (fileFirst.uploadStatus == null) FileUploadStatus.NONE
                        else FileUploadStatus.valueOf(fileFirst.uploadStatus),
                        hasGenerateChunk = fileFirst.hasGenerateChunk ?: false,
                        chunkFiles = fileRows.mapNotNull { row ->
                            row.chunkId?.let {
                                FileChunkModel(
                                    id = it,
                                    fileId = fileId,
                                    fileName = row.chunkFileName ?: "",
                                    originalFileName = row.chunkOriginalFileName ?: "",
                                    chunkNumber = row.chunkIndex?.toInt() ?: 0,
                                    blob = row.chunk,
                                    fileSize = (row.chunkFileSize ?: 0L).toInt(),
                                    chunkSize = (row.chunkSize ?: 0L).toInt(),
                                    totalFiles = row.totalFiles?.toInt() ?: 0
                                )
                            }
                        }
                    )
                }
        )
    }
}

fun List<GetFailedSubmissions>.toListAnswerModelFromFailedSubmissions(): List<Pair<AnswerModel, InspectionAnswerType>> {
    val grouped = this.groupBy { it.answerId }
    val result = grouped.map { (_, rows) ->
        val firstRow = rows.first()
        val files = rows.mapNotNull { row ->
            row.fileId?.let {
                AnswerFileModel(
                    id = it,
                    answerId = row.answerId,
                    name = row.fileName ?: "",
                    originalName = row.fileOriginalName ?: "",
                    path = row.filePath ?: "",
                    size = row.fileSize ?: 0,
                    type = row.fileType ?: "",
                    blob = row.fileBlob,
                    uploadStatus = if (row.fileStatus == null) FileUploadStatus.NONE
                    else FileUploadStatus.valueOf(row.fileStatus),
                    hasGenerateChunk = row.fileHasGenerateChunk ?: false
                )
            }
        }.distinctBy { it.id }

        val answerModel = AnswerModel(
            id = firstRow.answerId,
            inspectionId = firstRow.inspectionId,
            questionId = firstRow.questionId,
            notes = firstRow.notes ?: "",
            isRequiredFileOrNotes = firstRow.isRequiredFileOrNotes ?: false,
            answerDateIso = firstRow.answerDateIso ?: "",
            answerOptionId = firstRow.answerOptionId ?: "",
            answerText = firstRow.answerText ?: "",
            answerLocation = firstRow.locationId?.let {
                AnswerLocationModel(
                    id = it,
                    answerId = firstRow.answerId,
                    name = firstRow.locationName ?: "",
                    latitude = firstRow.latitude ?: 0.0,
                    longitude = firstRow.longitude ?: 0.0,
                )
            } ?: AnswerLocationModel(),
            answerSelection = firstRow.selectionId?.let {
                AnswerSelectionModel(
                    answerId = firstRow.answerId,
                    id = it,
                    answerSelectionId = firstRow.answerSelectionId ?: "",
                    answerSelectionValue = firstRow.answerSelectionValue ?: ""
                )
            } ?: AnswerSelectionModel(),
            files = files,
            lastUpdatedIsoUtc0 = firstRow.lastUpdated ?: "",
            isSubmitted = firstRow.isSubmitted ?: false,
            findingCategory = firstRow.findingCategory?.let {
                FindingCategory.safeValueOf(
                    it
                )
            }
        )

        val answerType = InspectionAnswerType.valueOf(firstRow.answerType)

        Pair(answerModel, answerType)
    }

    return result.filter { it.first.isFailedSubmit(it.second) }
}
